import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, ChevronDown, ChevronRight, Link2, Search, X } from "lucide-react";
import { cn } from "../../../../../lib/utils";
import {
    borderPrimary,
    primaryText,
    secondaryText,
    semiBoldText,
    smallTextSize,
    tertiaryText,
} from "../../../../../styling/stylingHelper";
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from "../../../../ui/InventorySlideOver";
import { eur } from "../../../../orderhistory/utils";
import SwapIngredientPanel from "../../review/SwapIngredientPanel";
import { candidateUnit, SwapCandidate } from "../../review/reviewAdapter";
import {
    createCaptureOnlyLinkAdapter,
    defaultLinkQuantity,
    summarizeCustomIngredientUsage,
    toReviewIngredient,
    usagesWithDefaultedQuantity,
} from "../../review/customLinkAdapter";
import { useCostPriceStore } from "../../../../../stores/appCostPriceStore";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import {
    commitCustomLinks,
    dryRunCustomLinks,
    CustomLinkCommitSummary,
    CustomLinkPlanRow,
    CustomLinkPlanSummary,
    CustomLinkResolution,
    CustomLinkUsage,
    IngredientQuantityInfo,
} from "../../../../../services/costPrice/costPriceService";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    restaurantId: string;
    /** Restaurant-wide default waste %, needed by the picker to cost subrecipe candidates. */
    defaultWastePercentage: number;
}

/**
 * Rows one run plans, mirroring the server's `CUSTOM_LINK_MAX_ROWS`
 * (`server/domains/costPrice/customLinkService.ts:74`, same cap as the inventory link
 * automation). The server truncates silently, so the review step says so when the plan
 * arrives at the cap — otherwise a restaurant with 500 custom ingredients links 400 and
 * never learns the rest exist.
 */
const CUSTOM_LINK_MAX_ROWS = 400;

// 'intro' states what will be analysed (the AI pass costs money, so it is one deliberate
// click); 'review' is the per-row bind step; 'done' reports what landed.
type Step = 'intro' | 'review' | 'done';

/**
 * A row's state in the review step. `pending` is UI-only and is NEVER sent: an untouched
 * row must stay custom so a later run offers it again, rather than being committed as a
 * guess. There is deliberately no `skip` — unlike waste (where a note may never be a
 * product), a custom ingredient always could be linked once it is actually bought, so
 * "not now" needs no durable state.
 */
type RowDecision =
    | { action: 'pending' }
    | {
        action: 'bind';
        platform: string;
        articleId: string;
        productName: string;
        quantityInfo: IngredientQuantityInfo;
        method: 'exact' | 'ai' | 'manual';
    }
    | { action: 'bindSubrecipe'; subrecipeId: string; displayName: string; portionsUnit?: string };

/** The unit a decision's quantities are expressed in — std unit, or portions for a subrecipe. */
function decisionUnit(decision: RowDecision): string | undefined {
    if (decision.action === 'bind') return decision.quantityInfo.stdUnit;
    if (decision.action === 'bindSubrecipe') return decision.portionsUnit;
    return undefined;
}

/**
 * Pre-fill one usage's quantity against the bound target, per README trap 3 and against
 * THAT usage's own frozen unit — `quantityInfo` is snapshotted at add time, so a custom
 * ingredient whose unit was edited later leaves older rows on the old unit.
 */
function seedQuantities(usages: CustomLinkUsage[], targetUnit: string | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    usages.forEach(usage => {
        out[usage.ingredientId] = defaultLinkQuantity(usage.quantityNeeded, usage.stdUnit, targetUnit).toString();
    });
    return out;
}

/**
 * "Link custom ingredients": bulk-re-points every custom ingredient in the restaurant's
 * cost model at a real order-history product (or a subrecipe). Modelled on
 * `pages/waste/WasteLinkModal.tsx` — the same dry-run → review → commit shape.
 *
 * THE DRY-RUN WRITES NOTHING. Custom ingredients stay attached to their analyses and
 * subrecipes for the whole review; the server sees the decisions once, on commit. This is
 * deliberately unlike the cost price automation review, which PUTs on every swap — which
 * is why the inline picker here gets a capture-only adapter instead of a write-through one.
 */
export default function LinkCustomIngredientsBulkModal({ isOpen, onClose, restaurantId, defaultWastePercentage }: Props) {
    const { t } = useTranslation();

    const costPriceList = useCostPriceStore(s => s.costPriceList);
    const customIngredients = useCostPriceStore(s => s.customIngredients);
    const subrecipesList = useSubrecipesStore(s => s.subrecipesList);

    const [step, setStep] = useState<Step>('intro');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [rows, setRows] = useState<CustomLinkPlanRow[]>([]);
    const [summary, setSummary] = useState<CustomLinkPlanSummary | null>(null);
    const [decisions, setDecisions] = useState<Record<string, RowDecision>>({});
    /** Raw per-usage quantity strings while typing: customKey → ingredientId → value. */
    const [quantities, setQuantities] = useState<Record<string, Record<string, string>>>({});
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [searchOpenFor, setSearchOpenFor] = useState<string | null>(null);
    const [commitSummary, setCommitSummary] = useState<CustomLinkCommitSummary | null>(null);

    // Fresh state on every open — an un-committed plan is stale the moment anything in the
    // cost model changes, which is also why it is never persisted to sessionStorage.
    useEffect(() => {
        if (!isOpen) return;
        setStep('intro');
        setBusy(false);
        setError(null);
        setRows([]);
        setSummary(null);
        setDecisions({});
        setQuantities({});
        setExpanded(new Set());
        setSearchOpenFor(null);
        setCommitSummary(null);
    }, [isOpen]);

    // What a run would analyse, counted from live store data so the intro is honest before
    // any AI budget is spent. Same canonical grouping the server plan uses.
    const introSummary = useMemo(() => summarizeCustomIngredientUsage({
        analyses: costPriceList,
        subrecipes: subrecipesList,
        customIngredients,
    }), [costPriceList, subrecipesList, customIngredients]);

    const runDryRun = async () => {
        setBusy(true);
        setError(null);
        try {
            const plan = await dryRunCustomLinks(restaurantId);
            setRows(plan.rows);
            setSummary(plan.summary);

            // Confident matches arrive pre-bound; everything else waits for a human.
            const nextDecisions: Record<string, RowDecision> = {};
            const nextQuantities: Record<string, Record<string, string>> = {};
            const nextExpanded = new Set<string>();
            for (const row of plan.rows) {
                if (row.status === 'auto' && row.best) {
                    nextDecisions[row.customKey] = {
                        action: 'bind',
                        platform: row.best.platform,
                        articleId: row.best.articleId,
                        productName: row.best.productName,
                        quantityInfo: row.best.quantityInfo,
                        method: row.best.method,
                    };
                    nextQuantities[row.customKey] = seedQuantities(row.usages, row.best.quantityInfo.stdUnit);
                    // Auto-expand exactly the rows whose quantities were DEFAULTED rather than
                    // derived, computed against the unit actually bound. A `pending` row has no
                    // target yet, so nothing has been guessed and there is nothing to show.
                    if (usagesWithDefaultedQuantity(row.usages, row.best.quantityInfo.stdUnit).length > 0) {
                        nextExpanded.add(row.customKey);
                    }
                } else {
                    nextDecisions[row.customKey] = { action: 'pending' };
                }
            }
            setDecisions(nextDecisions);
            setQuantities(nextQuantities);
            setExpanded(nextExpanded);
            setStep('review');
        } catch (err) {
            console.error('Custom ingredient link dry-run failed:', err);
            setError(t('costPrice.customLink.errorAnalyse'));
        } finally {
            setBusy(false);
        }
    };

    /** Bind a row and (re-)seed every usage's quantity against the new target's unit. */
    const bind = (row: CustomLinkPlanRow, decision: RowDecision) => {
        setDecisions(prev => ({ ...prev, [row.customKey]: decision }));
        if (decision.action === 'pending') {
            setQuantities(prev => {
                const next = { ...prev };
                delete next[row.customKey];
                return next;
            });
            return;
        }
        const targetUnit = decisionUnit(decision);
        setQuantities(prev => ({ ...prev, [row.customKey]: seedQuantities(row.usages, targetUnit) }));
        // Any bind can land on a unit type the usages do not share — a one-click suggestion
        // chip just as much as the picker. Open the list whenever this bind produced a
        // guessed amount, so it is never committed off-screen.
        setExpanded(prev => {
            if (usagesWithDefaultedQuantity(row.usages, targetUnit).length === 0) return prev;
            return new Set(prev).add(row.customKey);
        });
    };

    const setUsageQuantity = (customKey: string, ingredientId: string, value: string) => {
        if (value !== '' && !/^[0-9]*\.?[0-9]*$/.test(value)) return;
        setQuantities(prev => ({ ...prev, [customKey]: { ...(prev[customKey] ?? {}), [ingredientId]: value } }));
    };

    const toggleExpanded = (customKey: string) => setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(customKey)) next.delete(customKey); else next.add(customKey);
        return next;
    });

    /** The first usage whose quantity is missing or ≤ 0 — that row cannot be committed. */
    const blockingUsage = (row: CustomLinkPlanRow): CustomLinkUsage | null => {
        const rowQuantities = quantities[row.customKey] ?? {};
        for (const usage of row.usages) {
            const parsed = parseFloat(rowQuantities[usage.ingredientId] ?? '');
            if (!Number.isFinite(parsed) || parsed <= 0) return usage;
        }
        return null;
    };

    /** Rows that will actually be sent: bound AND with every usage quantity usable. */
    const committable = useMemo(() => rows.filter(row => {
        const decision = decisions[row.customKey];
        if (!decision || decision.action === 'pending') return false;
        return blockingUsage(row) === null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [rows, decisions, quantities]);

    const buildResolutions = (): CustomLinkResolution[] => committable.map(row => {
        const decision = decisions[row.customKey];
        const rowQuantities = quantities[row.customKey] ?? {};
        // Every usage sends its value, even an untouched pre-filled one: the commit never
        // infers a quantity server-side.
        const byIngredientId: Record<string, number> = {};
        row.usages.forEach(usage => { byIngredientId[usage.ingredientId] = parseFloat(rowQuantities[usage.ingredientId]); });

        if (decision.action === 'bindSubrecipe') {
            return {
                customKey: row.customKey,
                action: 'bindSubrecipe' as const,
                subrecipeId: decision.subrecipeId,
                portionsByIngredientId: byIngredientId,
            };
        }
        const bindDecision = decision as Extract<RowDecision, { action: 'bind' }>;
        return {
            customKey: row.customKey,
            action: 'bind' as const,
            platform: bindDecision.platform,
            articleId: bindDecision.articleId,
            productName: bindDecision.productName,
            quantityInfo: bindDecision.quantityInfo,
            quantityByIngredientId: byIngredientId,
            method: bindDecision.method,
        };
    });

    const runCommit = async () => {
        if (committable.length === 0) return;
        setBusy(true);
        setError(null);
        try {
            const result = await commitCustomLinks(restaurantId, buildResolutions());
            setCommitSummary(result);
            // Linked rows now price from order history; both stores hold stale copies.
            await Promise.all([
                useCostPriceStore.getState().reload(),
                useSubrecipesStore.getState().reload(),
            ]);
            setStep('done');
        } catch (err) {
            console.error('Custom ingredient link commit failed:', err);
            setError(t('costPrice.customLink.errorCommit'));
        } finally {
            setBusy(false);
        }
    };

    // Progress counts every row the planner did NOT auto-bind — the decisions a human owes.
    const reviewTotal = rows.filter(r => r.status !== 'auto').length;
    const reviewDone = rows.filter(r => {
        if (r.status === 'auto') return false;
        const decision = decisions[r.customKey];
        return !!decision && decision.action !== 'pending';
    }).length;
    const reviewPct = reviewTotal > 0 ? Math.round((reviewDone / reviewTotal) * 100) : 100;

    /** A unit is sometimes genuinely absent — say so rather than leaving a hole in the sentence. */
    const unitLabel = (unit: string | undefined) => (unit && unit.trim() !== '' ? unit : t('costPrice.customLink.noUnit'));

    const renderUsageList = (row: CustomLinkPlanRow) => {
        const decision = decisions[row.customKey];
        const targetUnit = decision ? decisionUnit(decision) ?? '' : '';
        const rowQuantities = quantities[row.customKey] ?? {};
        return (
            <div className="mt-2 space-y-1.5">
                {row.usages.map(usage => (
                    <div key={usage.ingredientId} className="flex items-center justify-between gap-2">
                        <p className={cn('text-[12px] min-w-0 truncate', secondaryText)}>
                            {usage.name}
                            {usage.kind === 'subrecipe' && (
                                <span className={cn('ml-1 text-[11px]', tertiaryText)}>
                                    {t('costPrice.subrecipes.subrecipeTag')}
                                </span>
                            )}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className={cn('text-[11px]', tertiaryText)}>
                                {usage.quantityNeeded} {usage.stdUnit} →
                            </span>
                            <input
                                type="number" inputMode="decimal" min={0} step="any"
                                value={rowQuantities[usage.ingredientId] ?? ''}
                                onChange={(e) => setUsageQuantity(row.customKey, usage.ingredientId, e.target.value)}
                                disabled={!decision || decision.action === 'pending'}
                                className="w-16 text-right bg-white dark:bg-background-dark border border-borderGrey dark:border-borderGrey-dark rounded px-2 py-0.5 text-[12px] font-medium text-[#0C111D] dark:text-textPrimary-dark outline-none disabled:opacity-50 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                            />
                            <span className={cn('text-[11px] w-6', tertiaryText)}>{targetUnit}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderRow = (row: CustomLinkPlanRow) => {
        const decision = decisions[row.customKey] ?? { action: 'pending' as const };
        const boundLabel = decision.action === 'bind'
            ? decision.productName
            : decision.action === 'bindSubrecipe' ? decision.displayName : null;
        const dishCount = row.usages.filter(u => u.kind === 'dish').length;
        const prepCount = row.usages.filter(u => u.kind === 'subrecipe').length;
        const isExpanded = expanded.has(row.customKey);
        const blocking = decision.action === 'pending' ? null : blockingUsage(row);
        // Usages whose pre-filled amount is a DEFAULT for the CURRENTLY BOUND target — not for
        // the server's `best`, which the user may well have replaced.
        const defaulted = decision.action === 'pending'
            ? []
            : usagesWithDefaultedQuantity(row.usages, decisionUnit(decision));

        return (
            <div key={row.customKey} className={cn('p-3 rounded-xl border', borderPrimary)}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className={cn(smallTextSize, semiBoldText, primaryText, 'truncate')}>{row.productName}</p>
                        <p className={cn('text-[11px]', tertiaryText)}>
                            {/* The custom ingredient's OWN stored price — stored, not derived, so it is
                                safe to show. No candidate € appears anywhere: a raw order-line price is
                                pre-factuur-override and would be a wrong number on screen. */}
                            {eur(row.currentPrice)}/{row.stdUnit} · {t(`costPrice.customLink.status_${row.status}`)}
                            {typeof row.best?.confidence === 'number' && row.best.confidence < 1 && (
                                <span className="ml-1 text-amber-600 dark:text-amber-400">
                                    · {t('costPrice.customLink.confidence', { pct: Math.round(row.best.confidence * 100) })}
                                </span>
                            )}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => setSearchOpenFor(prev => (prev === row.customKey ? null : row.customKey))}
                        className={cn(
                            'text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1 shrink-0',
                            borderPrimary,
                            searchOpenFor === row.customKey
                                ? 'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10 text-darkGreenColor dark:text-darkGreenColor-dark'
                                : secondaryText,
                            'hover:bg-gray-50 dark:hover:bg-gray-800'
                        )}
                    >
                        <Search className="w-3 h-3" /> {t('costPrice.search')}
                    </button>
                </div>

                <button
                    type="button"
                    onClick={() => toggleExpanded(row.customKey)}
                    className={cn('mt-1.5 flex items-center gap-1 text-[11px]', tertiaryText, 'hover:opacity-70')}
                >
                    {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {prepCount > 0
                        ? t('costPrice.customLink.usedInPreps', { dishes: dishCount, preps: prepCount })
                        : t('costPrice.customLink.usedIn', { dishes: dishCount })}
                </button>

                {defaulted.length > 0 && (
                    <p className="mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">
                        {/* A usage can legitimately store NO unit at all — name that case rather
                            than rendering the sentence with a hole in it. */}
                        {t('costPrice.customLink.unitChanged', {
                            from: Array.from(new Set(defaulted.map(u => unitLabel(u.stdUnit)))).join(', '),
                            to: unitLabel(decisionUnit(decision)),
                        })}
                    </p>
                )}

                {/* Its own line, never folded into `unitChanged`: the amount IS right here (a
                    `unit` source converts 1:1 to `p`), so the thing to confirm is the unit
                    nobody ever established, not an amount that was guessed. With no amber line
                    firing on these rows, this flag is the only thing marking them. */}
                {row.unknownSourceUnit && (
                    <p className={`mt-1.5 text-[11px] ${tertiaryText}`}>
                        {t('costPrice.customLink.unknownSourceUnit')}
                    </p>
                )}

                {row.alreadyOnDish && (
                    <p className={cn('mt-1.5 text-[11px]', tertiaryText)}>
                        {t('costPrice.customLink.bulkAlreadyOnDish')}
                    </p>
                )}

                {boundLabel && (
                    <div className="mt-2 flex items-center justify-between gap-2 p-1.5 pl-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                        <p className="min-w-0 text-[11px] text-green-700 dark:text-green-300 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3 shrink-0" />
                            <span className="truncate">{boundLabel}</span>
                        </p>
                        {/* Clears the match only — pure local state, no server call. The row simply
                            stays custom and is offered again on the next run. */}
                        <button
                            type="button"
                            onClick={() => bind(row, { action: 'pending' })}
                            className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/40 shrink-0"
                            aria-label={t('costPrice.customLink.clearMatch') as string}
                        >
                            <X className="w-3 h-3 text-green-700 dark:text-green-300" />
                        </button>
                    </div>
                )}

                {isExpanded && renderUsageList(row)}

                {blocking && (
                    <p className="mt-1.5 text-[11px] text-red-600 dark:text-red-400">
                        {t('costPrice.customLink.quantityRequired', { name: blocking.name })}
                    </p>
                )}

                {row.suggestions.length > 0 && (
                    <div className="mt-2 space-y-1">
                        {row.suggestions
                            .filter(s => s.productName !== boundLabel)
                            .map(s => (
                                <button
                                    key={`${s.platform}:${s.articleId}:${s.productName}`}
                                    type="button"
                                    onClick={() => bind(row, {
                                        action: 'bind',
                                        platform: s.platform,
                                        articleId: s.articleId,
                                        productName: s.productName,
                                        quantityInfo: s.quantityInfo,
                                        method: 'manual',
                                    })}
                                    className={cn('block w-full text-left px-2 py-1 rounded-lg border', borderPrimary, 'hover:bg-blue-50 dark:hover:bg-blue-900/20')}
                                >
                                    <span className="text-[11px] text-blue-600 dark:text-blue-400">+ {s.productName}</span>
                                    <span className={cn('block text-[10px]', tertiaryText)}>{s.supplier ?? s.platform}</span>
                                </button>
                            ))}
                    </div>
                )}

                {searchOpenFor === row.customKey && (
                    <SwapIngredientPanel
                        ingredient={toReviewIngredient({
                            productName: row.productName,
                            articleId: row.articleId,
                            platform: row.platform,
                            customPlatformName: row.customPlatformName,
                            // The first usage's own frozen amount/unit — the panel searches on the
                            // name and only uses these for its own prefill, which this flow replaces
                            // with the per-usage values below.
                            quantityNeeded: row.usages[0]?.quantityNeeded,
                            quantityInfo: { stdAmount: 1, stdMultiplier: 1, stdUnit: row.usages[0]?.stdUnit ?? row.stdUnit },
                        })}
                        ingredientIndex={-1}
                        itemId={row.customKey}
                        defaultWastePercentage={defaultWastePercentage}
                        adapter={createCaptureOnlyLinkAdapter((candidate: SwapCandidate) => {
                            bind(row, candidate.kind === 'subrecipe'
                                ? {
                                    action: 'bindSubrecipe',
                                    subrecipeId: candidate.subrecipeId!,
                                    displayName: candidate.displayName,
                                    portionsUnit: candidateUnit(candidate),
                                }
                                : {
                                    action: 'bind',
                                    platform: candidate.platform ?? '',
                                    articleId: candidate.articleId ?? '',
                                    productName: candidate.displayName,
                                    quantityInfo: candidate.quantityInfo ?? { stdAmount: 1, stdMultiplier: 1, stdUnit: 'p' },
                                    method: 'manual',
                                });
                            setSearchOpenFor(null);
                            // The panel's own quantity box is per-row; this flow commits a quantity
                            // PER USAGE, so open the list to show what will actually be written.
                            // (`bind` also opens it whenever the amounts were guessed; this covers
                            // the picker's own superseded input in every case.)
                            setExpanded(prev => new Set(prev).add(row.customKey));
                        })}
                        onDone={() => setSearchOpenFor(null)}
                    />
                )}
            </div>
        );
    };

    const getFooter = () => {
        if (step === 'intro') {
            return (
                <>
                    <button type="button" onClick={onClose} className={slideOverCancelClass}>
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={runDryRun}
                        disabled={busy || introSummary.used === 0}
                        className={slideOverPrimaryClass}
                    >
                        {busy ? t('costPrice.customLink.analysing') : t('costPrice.customLink.analyse')}
                    </button>
                </>
            );
        }
        if (step === 'review') {
            return (
                <>
                    <button type="button" onClick={() => setStep('intro')} disabled={busy} className={slideOverCancelClass}>
                        {t('common.back')}
                    </button>
                    <button
                        type="button"
                        onClick={runCommit}
                        disabled={busy || committable.length === 0}
                        className={slideOverPrimaryClass}
                    >
                        {busy ? t('costPrice.customLink.linking') : t('costPrice.customLink.linkCount', { n: committable.length })}
                    </button>
                </>
            );
        }
        return (
            <button type="button" onClick={onClose} className={slideOverPrimaryClass}>
                {t('common.close')}
            </button>
        );
    };

    return (
        <InventorySlideOver
            isOpen={isOpen}
            onClose={onClose}
            title={t('costPrice.customLink.bulkTitle')}
            description={t('costPrice.customLink.bulkDescription')}
            footer={getFooter()}
            closeLabel={t('common.cancel') as string}
        >
            {error && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <p className={cn(smallTextSize, 'text-red-700 dark:text-red-300')}>{error}</p>
                </div>
            )}

            {step === 'intro' && (
                <div className={cn('p-4 rounded-xl border', borderPrimary)}>
                    <p className={cn(smallTextSize, primaryText, semiBoldText)}>
                        {t('costPrice.customLink.introCount', {
                            n: introSummary.used,
                            usages: introSummary.dishUsages + introSummary.subrecipeUsages,
                        })}
                    </p>
                    <p className={cn('mt-2 text-[13px] leading-5', secondaryText)}>
                        {t('costPrice.customLink.introBody')}
                    </p>
                    {introSummary.orphans > 0 && (
                        <p className={cn('mt-2 text-[12px] leading-5', tertiaryText)}>
                            {t('costPrice.customLink.orphanNote', { n: introSummary.orphans })}
                        </p>
                    )}
                </div>
            )}

            {step === 'review' && summary && (
                <div>
                    {summary.total >= CUSTOM_LINK_MAX_ROWS && (
                        <p className="mb-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[12px] leading-5 text-amber-700 dark:text-amber-300">
                            {t('costPrice.customLink.bulkCapped', { n: CUSTOM_LINK_MAX_ROWS })}
                        </p>
                    )}

                    <div className="flex flex-wrap gap-2 mb-4">
                        {[
                            { label: t('costPrice.customLink.status_auto'), value: summary.auto },
                            { label: t('costPrice.customLink.status_review'), value: summary.review },
                            { label: t('costPrice.customLink.status_unmatched'), value: summary.unmatched },
                        ].map((chip, i) => (
                            <span key={i} className={cn(smallTextSize, 'px-3 py-1.5 rounded-lg border', borderPrimary, secondaryText)}>
                                {chip.label}: <span className={cn(semiBoldText, primaryText)}>{chip.value}</span>
                            </span>
                        ))}
                    </div>

                    {reviewTotal > 0 && (
                        <div className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-4 bg-card dark:bg-card-dark border-b border-zinc-200 dark:border-white/20">
                            <div className="flex items-center justify-between mb-1.5">
                                <span className={cn(smallTextSize, semiBoldText, primaryText)}>
                                    {t('costPrice.customLink.reviewProgress', { done: reviewDone, total: reviewTotal })}
                                </span>
                                <span className={cn('text-[11px]', tertiaryText)}>
                                    {reviewDone === reviewTotal ? t('costPrice.customLink.reviewAllDone') : `${reviewPct}%`}
                                </span>
                            </div>
                            <div className="h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300"
                                    style={{ width: `${reviewPct}%` }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="space-y-2">
                        {rows.map(renderRow)}
                    </div>

                    {rows.length === 0 && (
                        <p className={cn(smallTextSize, tertiaryText, 'text-center py-6')}>
                            {t('costPrice.customLink.nothingToLink')}
                        </p>
                    )}
                </div>
            )}

            {step === 'done' && commitSummary && (
                <div className={cn('p-4 rounded-xl border', borderPrimary)}>
                    <p className={cn(smallTextSize, semiBoldText, primaryText, 'flex items-center gap-2')}>
                        <Link2 className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark" />
                        {t('costPrice.customLink.doneLinked', {
                            n: commitSummary.linked,
                            dishes: commitSummary.dishesTouched,
                            preps: commitSummary.subrecipesTouched,
                        })}
                    </p>
                    {commitSummary.failed > 0 && (
                        <p className={cn('mt-3 text-[12px]', secondaryText)}>
                            {t('costPrice.customLink.doneFailed', { n: commitSummary.failed })}
                        </p>
                    )}
                </div>
            )}
        </InventorySlideOver>
    );
}
